import {
  AuthPlugin,
  EventInfo,
  EventServiceErrorType,
  EventServicePlugin,
  EventServicePluginError,
  jWebReady,
  LaunchServiceOptions,
  Publisher,
  ReservedValueStoreKeys,
  Service,
  ServiceId,
  ServiceInstance,
  ServiceInstanceEvent,
  ServiceList,
  ServiceRoutingError,
  ServiceRoutingPlugin,
  SubscribeOptions,
  Subscriber,
  SubscriptionHandle,
  ValueStoreGetResult,
  ValueStoreGetSuccessResult,
  ValueStorePlugin
} from '@jarvis/jweb-core'
import {
  DeviceSelectedForOobeDataInterface,
  SessionPayload,
  CreateSessionResponse,
  ResourceError,
  ExperienceType
} from '../interface/controllerInterface'

import { createSession, getPrinterInfo } from '../utils/api'
import {
  isObjectEmpty,
  Logger,
  odBaseUrl,
  simpleHash
} from '../utils/helperMethods'
import { AuthProvider } from '../utils/authProvider'
import {
  BootstrapErrorType,
  BootstrapEventType,
  ErrorCode,
  ErrorEventType
} from './bootstrap-events'
import { DeviceSupplier } from './DeviceProvider/DeviceSupplier'
import { SessionPayloadManager } from './SessionPayloadManager'
import * as DeviceResource from '@jarvis/react-device-resources-provider'
import { getDeviceResources } from './DeviceProvider/DeviceResourceRecipe'
import { DeviceResourceRecipeType } from './DeviceProvider/DeviceResourceRecipeType'
import { LaunchOptionsLoader, LaunchParam } from './LaunchOptionsLoader'
import {
  configureSplunkRum,
  setHpAppInstanceId,
  triggerSplunkCustomEvent
} from '../splunk/splunk-rum'
import {
  appVersion,
  appEnvironment,
  rumAuth,
  ldClientId
} from '../utils/appEnvironment'
import { SessionErrorInterface } from '../interface/sessionErrorInterface'
import { LaunchDarklyService } from '../utils/launchDarklyService'
import { FEATURE_FLAGS } from '../utils/featureFlags'
import { Buffer } from 'buffer'

const onboardingCenterServiceId = 'OnboardingCenter'

let Auth: AuthPlugin
let ServiceRouting: ServiceRoutingPlugin
let EventService: EventServicePlugin
let deviceSupplier: DeviceSupplier
let ValueStore: ValueStorePlugin
export let deviceSelectedForOobeEventSessionID: string
const onboardingDirectorBaseUrl = odBaseUrl()
let firstTimeGettingServices = true
let publisherIdRetryCounter = 1
const failedToLaunch = 'FAILED_TO_LAUNCH'

// Initial parameters
let launchParam: LaunchParam

export const getLaunchParam = () => launchParam

let launchedServiceInstance: ServiceInstance = null

// function to control if there is a service instance running
export const isServiceRunning = () => launchedServiceInstance !== null

export const setLaunchedServiceInstance = (
  serviceInstance: ServiceInstance | null
) => {
  launchedServiceInstance = serviceInstance
}

export const getOnboardingContextEntryQuery = () =>
  launchParam.onboardingContextEntry

const bootstrapControllerPublisherId = 'com.hp.jarvis.bootstrap.controller'

let bootstrapControllerPublisher: Publisher

export const setBootstrapControllerPublisher = (
  publisher: Publisher | EventServicePluginError
) => {
  if ((publisher as EventServicePluginError).errorType !== undefined)
    throw Error('Could not create publisher')
  bootstrapControllerPublisher = publisher as Publisher
}

export const resetPublisherIdRetryCounter = () => {
  publisherIdRetryCounter = 1
}

enum ClaimPostcardFlowState {
  // The postcard flow is enabled for this app and printer
  Enabled = 'enabled',
  // The postcard flow is not enabled for this app and printer
  Disabled = 'disabled',
  // The postcard flow must be evaluated for this app and printer
  MustEvaluate = 'mustEvaluate'
}

export const BootstrapController = async () => {
  const response = await jWebReady
  Logger.log('JWeb plugin response', response)

  if (isObjectEmpty(response.Plugins?.ServiceRouting)) {
    throw new Error('JWeb ServiceRouting plugin is not available to Bootstrap')
  }
  if (isObjectEmpty(response.Plugins?.Auth)) {
    await closeBootstrapService()
    throw new Error('JWeb Auth plugin is not available to Bootstrap')
  }
  if (isObjectEmpty(response.Plugins?.EventService)) {
    await closeBootstrapService()
    throw new Error('JWeb EventService plugin is not available to Bootstrap')
  }
  if (isObjectEmpty(response.Plugins?.Device)) {
    await closeBootstrapService()
    throw new Error('JWeb Device plugin is not available to Bootstrap')
  }
  if (isObjectEmpty(response.Plugins?.ValueStore)) {
    await closeBootstrapService()
    throw new Error('JWeb ValueStore plugin is not available to Bootstrap')
  }

  Auth = response.Plugins?.Auth
  ServiceRouting = response.Plugins?.ServiceRouting
  EventService = response.Plugins?.EventService
  deviceSupplier = await DeviceSupplier.create(response.Plugins?.Device)
  ValueStore = response.Plugins?.ValueStore

  await configureSplunkRum(
    appVersion,
    appEnvironment,
    rumAuth,
    ValueStore,
    deviceSupplier
  )

  Logger.log(
    { Auth: Auth },
    { ServiceRouting: ServiceRouting },
    { EventService: EventService },
    { ValueStore: ValueStore }
  )

  // Get the application name from the ValueStore
  const appName =
    (await getValueFromValueStore(ReservedValueStoreKeys.applicationName)) ??
    'unknown'
  const context = {
    kind: 'device',
    key: simpleHash(appName),
    appName: appName
  }
  Logger.log('LaunchDarkly contexts: ', context)
  LaunchDarklyService.initializeClient({
    clientId: ldClientId,
    context
  })

  // Creates a publisher to dispatch oobe bootstrap events
  const publisher = await getPublisher(
    EventService,
    bootstrapControllerPublisherId
  )

  if (publisher === null) {
    await closeBootstrapService()
    Logger.strError('Bootstrap publisher cannot be null')
    return
  }

  setBootstrapControllerPublisher(publisher)

  // load the data received
  try {
    const launchOptionsLoader: LaunchOptionsLoader = new LaunchOptionsLoader(
      ServiceRouting
    )
    await launchOptionsLoader.initialize()
    launchParam = launchOptionsLoader.loadLaunchOptions()
    if (launchParam.appInstanceId) {
      setHpAppInstanceId(launchParam.appInstanceId)
    }
  } catch (error) {
    Logger.strError(error)
    const err = { errorCode: ErrorCode.EBS00P0005 } as ResourceError
    publishBootstrapErrorEvent(BootstrapErrorType.startupError, err)
    return
  }

  // Subscribe to events
  const deviceSelectedForOobeEventSubscriber = await getSubscriber(
    EventService,
    'DeviceSelectedForOobe',
    handleDeviceSelectedForOobeEvent
  )

  const deviceSelectedToAddToCarouselSubscriber = await getSubscriber(
    EventService,
    'DeviceSelectedToAddToCarousel',
    handleDeviceSelectedForOobeEvent
  )

  const deviceSelectedForPostOobePrinterClaimSubscriber = await getSubscriber(
    EventService,
    'DeviceSelectedForPostOobePrinterClaim',
    handleDeviceSelectedForOobeEvent
  )

  //check if it's all right
  if (
    deviceSelectedForOobeEventSubscriber &&
    deviceSelectedToAddToCarouselSubscriber &&
    deviceSelectedForPostOobePrinterClaimSubscriber &&
    bootstrapControllerPublisher
  ) {
    //success
    Logger.log('Subscribed to all events')
    //dispatch Bootstrap Web App Ready event
    publish(
      bootstrapControllerPublisher,
      BootstrapEventType.BootstrapWebAppReady
    )
  }

  return 'react-oobe-bootstrap-controller'
}

const hasEventServicePluginError = (
  subscriber: Subscriber | EventServicePluginError
) => {
  Logger.strLog(
    'hasEventServicePluginError method called with object: ',
    subscriber
  )
  return (subscriber as EventServicePluginError).errorType !== undefined
}

const hasServiceRoutingError = (
  serviceRoutingError: ServiceList | ServiceRoutingError
) => {
  Logger.strLog(
    'hasServiceRoutingError method called with object: ',
    serviceRoutingError
  )
  return (serviceRoutingError as ServiceRoutingError).errorType !== undefined
}

const subscribe = (
  subscriber: Subscriber,
  eventName: string,
  eventHandler: (data) => void,
  publisherId?: string
): Promise<SubscriptionHandle | EventServicePluginError> => {
  Logger.log('subscribe method called')
  const subscriberOptions: SubscribeOptions = {
    eventName: eventName || null,
    publisherId: publisherId || null
  }
  Logger.log('subscribe options: ', subscriberOptions)
  return subscriber.subscribe(subscriberOptions, eventHandler)
}

const getSubscriber = async (
  eventService: EventServicePlugin,
  eventName: string,
  eventHandler: (data) => void,
  publisherId?: string
) => {
  Logger.log('getSubscriber method called')
  const eventSubscriber = await eventService.createSubscriber()
  let subscriptionHandler: EventServicePluginError | SubscriptionHandle

  if (!hasEventServicePluginError(eventSubscriber)) {
    // Unsubscribe to the specific event and then resubscribes to make sure the same
    // event doesn't have multiple subscriptions on the same subscriber
    const unsubscribeResult = await (eventSubscriber as Subscriber).unsubscribe(
      eventName
    )
    Logger.log('Unsubscribe result: ', unsubscribeResult)

    subscriptionHandler = await subscribe(
      eventSubscriber as Subscriber,
      eventName,
      eventHandler,
      publisherId
    )

    if (
      hasEventServicePluginError(subscriptionHandler as EventServicePluginError)
    ) {
      Logger.strError(
        'Failing to get subscriber [step subscriptionHandler] with error: ',
        (subscriptionHandler as EventServicePluginError).errorType,
        (subscriptionHandler as EventServicePluginError).reason
      )
    } else {
      Logger.log('Subscription handler: ', subscriptionHandler)
      return subscriptionHandler
    }
  } else {
    Logger.strError(
      'Failing to get subscriber [step eventSubscriber] with error: ',
      (eventSubscriber as EventServicePluginError).errorType,
      (eventSubscriber as EventServicePluginError).reason
    )
  }
}

export const getPublisher = async (
  eventService: EventServicePlugin,
  publisherId: string
) => {
  Logger.log('getPublisher method called')
  const publisher = await eventService.createPublisher(
    publisherId + publisherIdRetryCounter
  )

  if (hasEventServicePluginError(publisher as EventServicePluginError)) {
    const errorType = (publisher as EventServicePluginError).errorType
    Logger.log(
      'getPublisher method failed with: ',
      errorType,
      (publisher as EventServicePluginError).reason
    )

    /*
    Temporarily logic to handle publisherIdAlreadyInUse error.
    Remove once JWEB-5092 is resolved.
     */
    if (errorType === EventServiceErrorType.publisherIdAlreadyInUse) {
      publisherIdRetryCounter += 1
      return getPublisher(eventService, publisherId)
    }

    return null
  } else {
    return publisher as Publisher
  }
}

export const publish = async (
  publisher: Publisher,
  eventName: string,
  eventData: Record<string, unknown> = {}
) => {
  Logger.log('publish method called')
  const response = await publisher.publish(eventName, eventData)
  if (
    response &&
    hasEventServicePluginError(response as EventServicePluginError)
  ) {
    Logger.log(
      `Publishing event "${eventName}" failed`,
      (response as EventServicePluginError).errorType,
      (response as EventServicePluginError).reason
    )
    return false
  } else Logger.log(`Publishing ${eventName} Success`)
}

export const isServiceAvailable = async (
  serviceId: ServiceId
): Promise<Service | null> => {
  Logger.log('isServiceAvailable method called')
  const services = await getAvailableServices()
  const uiWebAppServiceId = services?.find(
    (service: Service) => service.id === serviceId
  )
  Logger.log('ui webapp serviceId: ', uiWebAppServiceId)
  if (uiWebAppServiceId) {
    return uiWebAppServiceId
  }
  return null
}

export const getAvailableServices = async (): Promise<Service[]> => {
  Logger.log('getAvailableServices method called')
  const serviceList = await ServiceRouting.getServices({
    requestRefresh: firstTimeGettingServices
  })
  Logger.log('Available services: ', serviceList)
  if (hasServiceRoutingError(serviceList)) {
    Logger.strError(
      `getAvailableServices failed with: ${
        (serviceList as ServiceRoutingError).errorType
      } - ${(serviceList as ServiceRoutingError).reason}`
    )
    return [] as Service[]
  } else {
    firstTimeGettingServices = false
    return (serviceList as ServiceList).services
  }
}

export const createOnboardingAgentServicedSubscribers = async (
  serviceInstance: ServiceInstance
) => {
  //saving current service instance created to avoid concurrent services
  setLaunchedServiceInstance(serviceInstance)

  const closeSubscriber = await getSubscriber(
    EventService,
    ServiceInstanceEvent.jarvisEventServiceInstanceClosed,
    handleServiceInstanceEvent,
    launchedServiceInstance.eventPublisherId
  )
  Logger.log('Service instance closed subscriber: ', closeSubscriber)

  const changedSubscriber = await getSubscriber(
    EventService,
    // For some reason if we user `ServiceInstanceEvent.jarvisEventServiceInstanceStateChanged`
    // the value is returned as `undefined`
    'com.hp.jarvis.servicerouting.serviceinstance.statechanged',
    handleServiceInstanceEvent,
    launchedServiceInstance.eventPublisherId
  )
  Logger.log('Service instance state change subscriber: ', changedSubscriber)
}

export const handleServiceInstanceEvent = async (eventInfo: EventInfo) => {
  //
  Logger.log('handleServiceInstanceEvent() called')
  const { subscriber, publisherId, eventName, eventData } = eventInfo
  Logger.log(
    `Event with ${eventName}/${subscriber}/${publisherId}/${eventData} was fired`
  )
  Logger.log('EventInfo: ', eventInfo)
  Logger.log('EventData: ', eventData)

  // checking if the retained service instance is the same as the event is referencing
  if (
    launchedServiceInstance?.eventPublisherId === publisherId &&
    launchedServiceInstance?.instanceId === eventData['serviceInstanceId']
  ) {
    Logger.log('publisherId matches launched service')

    switch (eventName) {
      case ServiceInstanceEvent.jarvisEventServiceInstanceClosed:
        // Releasing the reference of the closed service
        setLaunchedServiceInstance(null)
        // Closing Bootstrap
        await closeBootstrapService()
        break
      case ServiceInstanceEvent.jarvisEventServiceInstanceStateChanged:
        if (eventData['state'] === failedToLaunch) {
          Logger.strError(
            'Error on handleServiceInstanceEvent: ',
            `Error launching OnboardingCenter. ServiceRouting returned: ${failedToLaunch}`
          )
          publishOnboardingCenterUnavailableEvent(ErrorCode.EBS00P0001)

          // Releasing the reference of the closed service
          setLaunchedServiceInstance(null)
          // Closing Bootstrap
          await closeBootstrapService()
        }
        break
    }
  }
}

export const closeBootstrapService = async () => {
  Logger.log('Bootstrap will close itself')
  if (launchParam?.wasLoadedFromServiceRouting ?? false) {
    try {
      ServiceRouting.closeServiceInstance()
    } catch (error) {
      Logger.strError('Error while closing Bootstrap', error)
    }
  }
}

export const handleDeviceSelectedForOobeEvent = async (
  eventInfo: EventInfo
) => {
  Logger.log('serviceIsRunning: ', isServiceRunning())
  if (isServiceRunning()) return
  Logger.log('DeviceSelectedForOobeEvent: ', eventInfo)

  const {
    Connectivity,
    SessionId
  } = eventInfo.eventData as DeviceSelectedForOobeDataInterface
  deviceSelectedForOobeEventSessionID = SessionId

  const useClaimPostcardFlow = await shouldUseClaimPostcardFlow(SessionId)
  Logger.log('shouldUseClaimPostcardFlow response: ', useClaimPostcardFlow)

  const useOnlyRequestCdmClaimPostcard = await LaunchDarklyService.getFlagValue(
    FEATURE_FLAGS.ENABLE_ONLY_REQUEST_CDM_CLAIM_POSTCARD,
    true
  )

  // Determine the recipe type based on feature flags
  // If using claim postcard flow, check if only CDM resources should be requested
  // else use standard Bootstrap recipe
  const recipeType = useClaimPostcardFlow
    ? useOnlyRequestCdmClaimPostcard
      ? DeviceResourceRecipeType.BootstrapCDMOnlyPostcard
      : DeviceResourceRecipeType.BootstrapPostcard
    : DeviceResourceRecipeType.Bootstrap

  const result:
    | DeviceResource.DeviceResource[]
    | ResourceError = await getDeviceResources(
    SessionId,
    recipeType,
    launchParam.isDebugMode
  )

  const isValidDeviceResources = Array.isArray(result) && result.length !== 0

  if (!isValidDeviceResources) {
    const error = result as ResourceError
    Logger.strError('getDeviceResources returned an error: ', error)
    publishBootstrapErrorEvent(
      BootstrapErrorType.deviceHttpProxyCallError,
      error,
      SessionId
    )
    return
  }
  const deviceResources = result as DeviceResource.DeviceResource[]
  Logger.log('getDeviceResources response: ', deviceResources)

  // Convert CDM resources from object to base64 string for claim postcard resources if feature flag is enabled
  const processedDeviceResources = await convertClaimPostcardCdmToBase64(
    deviceResources
  )

  // Prefetch a list of pre defined resources that won't be used by Bootstrap (fire-and-forget).
  // Called to fill the DeviceHTTPProxy cache so other application can use the cached data.
  const shouldPrefetchCDM = await LaunchDarklyService.getFlagValue(
    FEATURE_FLAGS.CDM_PREFETCH,
    true
  )

  if (shouldPrefetchCDM) {
    // Prefetch CDM resources
    getDeviceResources(
      SessionId,
      DeviceResourceRecipeType.CDM,
      launchParam.isDebugMode
    )
      .then((response) => {
        Logger.log(`getDeviceResourcesCDM fulfilled response: ${response}`)
      })
      .catch((error) => {
        Logger.log(`getDeviceResourcesCDM rejected response: ${error}`)
      })
  }

  const shouldPrefetchLEDM = await LaunchDarklyService.getFlagValue(
    FEATURE_FLAGS.LEDM_PREFETCH,
    true
  )

  if (shouldPrefetchLEDM) {
    // Prefetch LEDM resources
    getDeviceResources(
      SessionId,
      DeviceResourceRecipeType.LEDM,
      launchParam.isDebugMode
    )
      .then((response) => {
        Logger.log(`getDeviceResourcesLEDM fulfilled response: ${response}`)
      })
      .catch((error) => {
        Logger.log(`getDeviceResourcesLEDM rejected response: ${error}`)
      })
  }

  const sessionPayloadManager: SessionPayloadManager = await SessionPayloadManager.create(
    deviceSupplier
  )

  const shouldInformationEventType = await LaunchDarklyService.getFlagValue(
    FEATURE_FLAGS.ENABLE_POST_OOBE_FLOW,
    false
  )

  const eventType: ExperienceType | undefined = shouldInformationEventType
    ? (eventInfo.eventName as ExperienceType)
    : undefined

  // Creates the Session Payload, even with empty resources
  const sessionPayload: SessionPayload = await sessionPayloadManager.createSessionPayload(
    launchParam.clientId,
    Connectivity,
    processedDeviceResources,
    launchParam,
    eventType
  )

  // Logs as error to show on Splunk RUM
  Logger.strError(
    'sessionPayload.onboardingContext: ',
    sessionPayload.onboardingContext
  )
  Logger.strError('createSessionPayload.app: ', sessionPayload.app)
  Logger.strError(
    'createSessionPayload.device.connectivityToApp: ',
    sessionPayload.device.connectivityToApp
  )

  sessionPayload.device.deviceResources.forEach((resource, index) => {
    Logger.strError(
      `serviceMetadata for resource[${index}]: `,
      resource.serviceMetadata
    )
    Logger.strError(
      `resource[${index}]: `,
      resource.ledmResource ?? resource.cdmResource
    )
  })

  Logger.log('onboardingDirectorBaseUrl: ', onboardingDirectorBaseUrl)

  const shouldLogTokenValue = await LaunchDarklyService.getFlagValue(
    FEATURE_FLAGS.ENABLE_USER_TOKEN_LOGGING,
    false
  )

  const authProvider = new AuthProvider(Auth, shouldLogTokenValue)

  let createSessionResponseData: CreateSessionResponse

  if (launchParam.nextService) {
    createSessionResponseData = {
      sessionContext: {
        sessionId: SessionId,
        onboardingContext: sessionPayload.onboardingContext,
        app: sessionPayload.app
      },
      nextService: {
        serviceId: launchParam.nextService,
        resultUrl: ''
      }
    }
    Logger.log(
      'Ignoring Onboarding Director request and responding, event: ',
      createSessionResponseData.event
    )
    Logger.log(
      'Ignoring Onboarding Director request and responding, nextService: ',
      createSessionResponseData.nextService
    )
    Logger.log(
      'Ignoring Onboarding Director request and responding, sessionContext: ',
      createSessionResponseData.sessionContext
    )
  } else {
    const responseData = await executeCreateSessionFlow(
      authProvider,
      sessionPayload,
      SessionId,
      0
    )

    if (responseData !== undefined) {
      createSessionResponseData = responseData
    } else {
      return
    }
  }

  if (createSessionResponseData.event) {
    publish(bootstrapControllerPublisher, createSessionResponseData.event, {
      appSessionId: SessionId
    })
  }

  if (createSessionResponseData.nextService) {
    await startOnboardingFlow(SessionId, createSessionResponseData)
  }
}

export const executeCreateSessionFlow = async (
  authProvider: AuthProvider,
  sessionPayload: SessionPayload,
  SessionId,
  retryNumber: number
) => {
  try {
    const createSessionResponse = await createSession(
      onboardingDirectorBaseUrl,
      authProvider,
      sessionPayload
    )

    Logger.strLog('createSessionResponse: ', createSessionResponse)

    return createSessionResponse.data
  } catch (createSessionError) {
    if (createSessionError.message === 'Network Error' && retryNumber < 2) {
      Logger.warn('Network error triggered, will retry')
      const response = executeCreateSessionFlow(
        authProvider,
        sessionPayload,
        SessionId,
        retryNumber + 1
      )
      return response
    } else {
      Logger.warn('Network error triggered too many times, will NOT retry')
      await handleCreateSessionError(SessionId, createSessionError)
    }
  }
}

const handleCreateSessionError = async (SessionId, createSessionError) => {
  logCreateSessionError(createSessionError)
  const reason = extractReasonFromError(createSessionError)
  const { data, status } = createSessionError.response ?? {}
  const event = data?.event
  if (status === 404) {
    const eventToPublish = event || BootstrapEventType.NoPlan
    Logger.log('Should publish event: ', eventToPublish)
    publish(bootstrapControllerPublisher, eventToPublish, {
      appSessionId: SessionId,
      reason
    })
    triggerSplunkCustomEvent(reason)
    await closeBootstrapService()
  } else if (!status) {
    Logger.log('Should publish event: ', ErrorEventType.BootstrapError)
    const err = { errorCode: ErrorCode.EBS00P0009 } as ResourceError
    publishBootstrapErrorEvent(BootstrapErrorType.networkError, err)
    triggerSplunkCustomEvent(ErrorCode.EBS00P0009)
  } else {
    Logger.log(
      'Should publish event: ',
      ErrorEventType.OnboardingDirectorUnavailable
    )
    publish(
      bootstrapControllerPublisher,
      ErrorEventType.OnboardingDirectorUnavailable,
      {
        appSessionId: SessionId,
        statusCode: status ?? 0,
        reason
      }
    )
    triggerSplunkCustomEvent(reason)
    await closeBootstrapService()
  }
  Logger.log('Should have published an event')
}

const logCreateSessionError = (createSessionError) => {
  if (createSessionError.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    Logger.strError(
      'CreateSessionError response.data: ',
      createSessionError.response.data
    )
    Logger.error(
      'CreateSessionError response.status: ',
      createSessionError.response.status
    )
    Logger.error(
      'CreateSessionError response.headers: ',
      createSessionError.response.headers
    )
  } else if (createSessionError.request) {
    // The request was made but no response was received
    // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
    // http.ClientRequest in node.js
    Logger.error('CreateSessionError request: ', createSessionError.request)
  } else {
    // Something happened in setting up the request that triggered an Error
    Logger.error('CreateSessionError message: ', createSessionError.message)
  }
  Logger.strError('CreateSessionError config: ', createSessionError.config)
}

export const startOnboardingFlow = async (
  appSessionId: string,
  createSessionResponseData: CreateSessionResponse
) => {
  //
  Logger.log('Starting onboarding flow...')

  let serviceInstance

  if (launchParam.onboardingCenter) {
    const openUrlServiceId = 'openUrl'
    const openURLIsAvailable = await isServiceAvailable(openUrlServiceId)
    if (!openURLIsAvailable) {
      Logger.strError(`${openUrlServiceId} service is not available!`)
      return false
    }
    const launchServiceOptions: LaunchServiceOptions = {
      serviceId: openUrlServiceId,
      serviceOptions: {
        url: launchParam.onboardingCenter,
        onboardingContext: createSessionResponseData,
        appSessionId
      }
    }
    Logger.log(
      'Will try to follow Query Parameters flow ',
      launchServiceOptions
    )

    serviceInstance = await ServiceRouting.launchService(launchServiceOptions)
  } else {
    const serviceIsAvailable = await isServiceAvailable(
      onboardingCenterServiceId
    )

    if (!serviceIsAvailable) {
      Logger.strError(
        'Error on startOnboardingFlow: ',
        `${onboardingCenterServiceId} service is not available!`
      )
      publishOnboardingCenterUnavailableEvent(ErrorCode.EBS00P0002)
      return false
    }

    const launchServiceOptions: LaunchServiceOptions = {
      serviceId: onboardingCenterServiceId,
      serviceOptions: {
        appSessionId,
        onboardingContext: createSessionResponseData
      }
    }

    Logger.log('Will try to launchService with options ', launchServiceOptions)
    serviceInstance = await ServiceRouting.launchService(launchServiceOptions)
  }

  Logger.log('ServiceRouting launchService response: ', serviceInstance)
  if (hasServiceRoutingError(serviceInstance)) {
    Logger.strError(
      'Error on startOnboardingFlow: ',
      `Error while launching service: ${
        (serviceInstance as ServiceRoutingError).errorType
      } - ${(serviceInstance as ServiceRoutingError).reason}`
    )
    publishOnboardingCenterUnavailableEvent(ErrorCode.EBS00P0003)
  } else {
    Logger.log(
      `Launched service ${
        (serviceInstance as ServiceInstance).serviceId
      } as launched service instance ${
        (serviceInstance as ServiceInstance).instanceId
      }`
    )
    //subscribe for closed event handler
    createOnboardingAgentServicedSubscribers(serviceInstance as ServiceInstance)
  }
}

export const publishOnboardingCenterUnavailableEvent = async (
  reason: string
) => {
  Logger.strError(reason)
  publish(
    bootstrapControllerPublisher,
    ErrorEventType.OnboardingCenterUnavailable,
    {
      errorType: BootstrapErrorType.startupError,
      reason
    }
  )
  triggerSplunkCustomEvent(reason)
}

export const publishBootstrapErrorEvent = async (
  errorType: BootstrapErrorType,
  reason: ResourceError,
  appSessionId?: string
) => {
  Logger.strError('Bootstrap Error Event with reason: ', reason.errorCode)
  publish(bootstrapControllerPublisher, ErrorEventType.BootstrapError, {
    appSessionId,
    errorType: errorType,
    reason: reason.errorCode
  })
  triggerSplunkCustomEvent(reason.errorCode, reason)
  await closeBootstrapService()
}

export const extractReasonFromError = (
  createSessionError: SessionErrorInterface
) => {
  const { data, status } = createSessionError.response ?? {}
  if (data?.errors?.length > 0) {
    return data.errors.map((error) => error.code).join('; ')
  } else {
    return getErrorCode(
      createSessionError?.message?.includes('timeout')
        ? ErrorCode.EBS00P0008
        : ErrorCode.EBS00P1XXX,
      status
    )
  }
}

/**
 * Get the error code string to each ErrorCode
 * @param errorCode the error code case
 * @param statusCode the optional status code returned. Required only if ErrorCode is EBS00P1XXX.
 * @returns the error code string
 */
export const getErrorCode = (
  errorCode: ErrorCode,
  statusCode?: string | number | null
) => {
  if (errorCode === ErrorCode.EBS00P1XXX) {
    let trailingError = statusCode
    if (statusCode === undefined || statusCode === null) {
      trailingError = 'XXX'
    }
    return 'EBS00P1' + trailingError
  } else {
    return errorCode
  }
}

/**
 * Check if the claim postcard flow should be used
 * @param SessionId The session identifier
 * @returns {Promise<boolean>} True if the claim postcard flow should be used, false otherwise
 */
export const shouldUseClaimPostcardFlow = async (
  SessionId: string
): Promise<boolean> => {
  // Check if the ClaimPostcard flow is enabled
  const shouldValidate = await isClaimPostcardEnabledForAppAndPrinter()
  switch (shouldValidate) {
    case ClaimPostcardFlowState.Enabled:
      return true
    case ClaimPostcardFlowState.Disabled:
      return false
    case ClaimPostcardFlowState.MustEvaluate:
      break
  }

  Logger.log(
    'ClaimPostcard flow must be evaluated for app and printer, will try to get printer product number'
  )

  // Get the printer product number
  let productNumber
  try {
    productNumber = await getPrinterProductNumber(SessionId)
  } catch (error) {
    Logger.strError('Error getting printer product number: ', error.message)
    // If there is an error getting the product number, should use the ClaimPostcard flow
    return true
  }

  // If not product number is found, should use the ClaimPostcard flow
  if (!productNumber) {
    Logger.strError('No product number found')
    return true
  }

  // Check the OnboardingCapabilities for the product number
  let printerInfo

  try {
    const authProvider = new AuthProvider(Auth, false)
    printerInfo = await getPrinterInfo(authProvider, productNumber)
  } catch (error) {
    Logger.strError('Error getting printer info: ', error.message)
    // If there is an error getting the printer info, should use the ClaimPostcard flow
    return true
  }

  // For cases where it does not have it, should use the ClaimPostcard flow
  if (!printerInfo?.onboardingCapabilities) {
    Logger.strError(
      'No onboardingCapabilities found for printer info, should use ClaimPostcard flow'
    )
    return true
  }

  // Checks if the smallville onboarding capabilities are allowed for this printer
  const isAllowed = printerInfo?.onboardingCapabilities?.allowedClients?.includes(
    'smallville'
  )
  // Checks if the smartWin onboarding capabilities are prohibited for this printer
  const isProhibited = printerInfo?.onboardingCapabilities?.prohibitedClients?.includes(
    'smartWin'
  )

  // If the printer is allowed for smallville and prohibited for smartWin, it is a SMB printer
  const isSMBPrinter = isAllowed && isProhibited

  // If a SMB printer is found, it should not follow the ClaimPostcard flow
  return !isSMBPrinter
}

/**
 * Check if the ClaimPostcard flow is enabled
 * @returns {Promise<boolean>} True if the ClaimPostcard flow is enabled, false otherwise
 */
const isClaimPostcardEnabledForAppAndPrinter = async (): Promise<ClaimPostcardFlowState> => {
  // Check if LaunchDarkly feature flag is enabled
  const isClaimPostcardFlowEnabled = await LaunchDarklyService.getFlagValue(
    FEATURE_FLAGS.ENABLE_CLAIM_POSTCARD_FLOW,
    true
  )

  // If the feature flag is not enabled, return false
  if (!isClaimPostcardFlowEnabled) return ClaimPostcardFlowState.Disabled

  // Check if the app allows SMB printer onboarding
  // List of apps that allow SMB printer onboarding
  const smbAppList = ['smallville']
  const appName = await getValueFromValueStore(
    ReservedValueStoreKeys.applicationName
  )
  Logger.log('App name: ', appName)
  const isSMBCapableApp =
    smbAppList.find((name) => appName?.toLowerCase().includes(name)) ?? false

  // If app is not SMB capable and the isClaimPostcardFlowEnabled is enabled, should use the ClaimPostcard flow
  Logger.log('isSMBCapableApp', isSMBCapableApp)
  if (!isSMBCapableApp) return ClaimPostcardFlowState.Enabled

  // Check if LaunchDarkly feature flag is enabled for SMB capable apps
  const isClaimPostcardFlowEnabledOnSMBCapableApps = await LaunchDarklyService.getFlagValue(
    FEATURE_FLAGS.ENABLE_CLAIM_POSTCARD_FLOW_FOR_SMB_CAPABLE_APPS,
    true
  )

  // Arriving here means that the app is SMB capable.
  // If the feature flag is enabled, should continue with the ClaimPostcard flow validation
  // If the feature flag is not enabled, should stop and not use the ClaimPostcard flow
  return isClaimPostcardFlowEnabledOnSMBCapableApps
    ? ClaimPostcardFlowState.MustEvaluate
    : ClaimPostcardFlowState.Disabled
}

/**
 * Get the printer product number from the device resources
 * @param SessionId The session identifier
 * @returns {Promise<string | undefined>} The printer product number or undefined if not found
 */
export const getPrinterProductNumber = async (
  SessionId: string
): Promise<string | undefined> => {
  let productNumber: string | undefined
  // Fetch device resources that contain the ProductNumber
  const resources = (await getDeviceResources(
    SessionId,
    DeviceResourceRecipeType.ProductNumber,
    launchParam.isDebugMode
  )) as DeviceResource.DeviceResource[]

  Logger.log('Did get device resources for ProductNumber')

  if (!resources || resources.length === 0) {
    Logger.strError('No resources found for ProductNumber')
    return productNumber
  }

  // Tries the CDM approach first
  const identityResource = resources.find((resource) => {
    return (
      resource.serviceMetadata.rel &&
      resource.serviceMetadata.rel === 'identity'
    )
  })
  if (identityResource) {
    Logger.log('Found identity resource, will extract product number')
    // Set the product number from the identity resource
    productNumber = (identityResource.cdmResource as {
      productNumber?: string
    })?.productNumber
  } else {
    // Tries the LEDM approach
    const ProductConfigDynResource = resources.find((resource) => {
      return (
        resource.serviceMetadata.serviceGun &&
        resource.serviceMetadata.serviceGun.includes('ProductConfigDyn')
      )
    })
    if (ProductConfigDynResource) {
      // Decode the ProductConfigDyn resource from base64
      const decodedResource = Buffer.from(
        ProductConfigDynResource.ledmResource,
        'base64'
      ).toString('utf-8')
      // Parse the decoded resource as XML
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(decodedResource, 'text/xml')
      // Extract the product number from the XML
      const productNumberElement = xmlDoc.getElementsByTagName(
        'dd:ProductNumber'
      )[0]
      // Set the product number from the ProductConfigDyn resource
      productNumber = productNumberElement
        ? productNumberElement.textContent
        : undefined
    }
  }

  return productNumber
}

const getValueFromValueStore = async (
  key: string
): Promise<string | undefined> => {
  const result = (await ValueStore.get({
    keys: [key]
  })) as ValueStoreGetResult
  // Get the value from the ValueStore
  return result?.result?.length
    ? (result.result[0] as ValueStoreGetSuccessResult).value
    : undefined
}

/**
 * Converts CDM resources from JSON object to base64 string for claim postcard resources
 * @param deviceResources The device resources array to process
 * @returns {Promise<DeviceResource.DeviceResource[]>} The processed device resources
 */
const convertClaimPostcardCdmToBase64 = async (
  deviceResources: DeviceResource.DeviceResource[]
): Promise<DeviceResource.DeviceResource[]> => {
  // Check if the feature flag is enabled
  const isConversionEnabled = await LaunchDarklyService.getFlagValue(
    FEATURE_FLAGS.ENABLE_CLAIM_POSTCARD_CDM_BASE64_CONVERSION,
    false
  )

  if (!isConversionEnabled) {
    return deviceResources
  }

  return deviceResources.map((resource) => {
    // Check if this resource has rel equal to "claimPostcard" and has a cdmResource that's an object
    if (
      resource.serviceMetadata?.rel === 'claimPostcard' &&
      resource.cdmResource &&
      typeof resource.cdmResource === 'object' &&
      typeof resource.cdmResource !== 'string'
    ) {
      Logger.log(
        'Converting CDM resource from object to base64 string for claim postcard resource'
      )
      // Convert object to JSON string, then encode to base64
      const jsonString = JSON.stringify(resource.cdmResource)
      const base64String = Buffer.from(jsonString, 'utf-8').toString('base64')

      return {
        ...resource,
        cdmResource: (base64String as unknown) as object
      }
    }
    return resource
  })
}
